import * as React from 'react';
import { useStyles } from './Card.styles';
import { ResponsiveStyleValue, SpacingValue, useComponentDefaultProps } from '../../theming';
import { Paper, PaperProps } from '../Paper/Paper';
import { Styler, StylerProps } from '../Styler/Styler';
import { FlagProps } from '../Flag/Flag';
import { CardOrientation } from '../../theming/types/components/Card.types';
import { CardContext, CardContextValues } from './Card.context';
import { CardHeader } from './CardHeader/CardHeader';
import { CardSection } from './CardSection/CardSection';
import { Dimmer } from '../Dimmer';

export interface CardProps extends StylerProps {
    /**
     * Any card adornments. These will overlay other card content. Position these using the style system props.
     */
    adornments?: React.ReactElement<FlagProps> | Array<React.ReactElement<FlagProps>>;

    /**
     * The card content.
     */
    children: React.ReactNode;

    /**
     * The orientation of the card.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default vertical
     */
    orientation?: ResponsiveStyleValue<CardOrientation>;

    /**
     * The padding inside the card. This will be implemented by nested `CardSection` components.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 4
     */
    padding?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Additional properties applied to the `Paper` component.
     */
    SurfaceProps?: PaperProps;

    /**
     * If `true`, the card will be appear in the `selected` style.
     * @default false
     */
    selected?: boolean;

    /**
     * If `true`, a dimmer will be applied over the card.
     * @default false
     */
    dimmed?: boolean;
}

const defaultProps: Partial<CardProps> = {
    component: 'article',
    orientation: 'portrait',
    padding: 5,
};

/**
 * The following card component is a generic card component which can be used in combination with card headers and section to
 * help and create the various cards needed to be used across the website. You can see some of the examples of cards below in variants.
 * The card component supports for styler props to customise the card styling and additionally can support for selected state as well as
 * providing any adornments to the card to be displayed within the card. We can also control the orientation of the card (portrait or
 * landscape) from the props which allows us more flexibility.
 */
export function Card(props: Readonly<CardProps>): React.JSX.Element {
    const {
        adornments,
        children,
        orientation = 'portrait',
        padding,
        SurfaceProps,
        selected = false,
        dimmed = false,
        ref,
        ...other
    } = useComponentDefaultProps('Card', defaultProps, props);

    const { styles } = useStyles({ orientation });

    const contextValues: CardContextValues = React.useMemo(() => {
        return { padding, orientation, selected };
    }, [padding, orientation, selected]);

    let cardHeader;
    const cardSections: React.ReactNode[] = [];
    const otherChildren: React.ReactNode[] = [];

    React.Children.forEach(children, (element) => {
        switch (React.isValidElement(element) && element.type) {
            case CardHeader:
                cardHeader = element;
                break;
            case CardSection:
                // Card sections need to be wrapped in their own container in order for the orientaton prop to work.
                cardSections.push(element);
                break;
            default:
                otherChildren.push(element);
                break;
        }
    });

    return (
        <Styler css={styles.root} {...other}>
            <Paper ref={ref} css={styles.surface} data-selected={selected || undefined} {...SurfaceProps}>
                <CardContext.Provider value={contextValues}>
                    <>
                        {cardHeader || null}
                        {cardSections.length > 0 && <div css={styles.sections}>{cardSections}</div>}
                        {otherChildren.length > 0 && otherChildren}
                    </>
                </CardContext.Provider>
            </Paper>
            {adornments &&
                React.Children.map(adornments, (element) =>
                    React.cloneElement(element, {
                        pos: 'absolute',
                        zIndex: 1,
                    })
                )}
            <Dimmer active={dimmed} />
        </Styler>
    );
}
